import React, { useEffect, useMemo } from 'react';
import {
  type ICampaignInfo,
  useAutoJoin,
  useCampaignCardState,
  useCampaignCardStyles,
  useCampaignDataAttributes,
  normalizeCampaignAssets,
  PRIZE_POP_CAMPAIGN_TYPE,
  useCampaignsStore,
} from 'features/campaigns';
import { useCampaignStatus, useCollapseAnimation } from 'features/campaigns/hooks';
import CampaignCardBackground from './components/CampaignCardBackground';
import CampaignCardButton from './components/CampaignCardButton';
import CampaignCardControls from './components/CampaignCardControls';
import './css/campaign-card-variables.module.css';
import originalHasManyStyles from './css/hasManyStyles.module.css';
import type { CampaignCard as CampaignCardStyles } from './css/CampaignCard.modules';

interface CampaignCardProps {
  campaignInfo: ICampaignInfo;
  onClick?: () => void;
}

const CampaignCard: React.FC<CampaignCardProps> = ({ campaignInfo, onClick }) => {
  const { getCampaignCardStyles } = useCampaignCardStyles();
  const campaignCardStyles = getCampaignCardStyles();
  const attributes = useCampaignDataAttributes({
    campaignType: campaignInfo.type,
    additionalProps: { 'data-variant': 'card' }
  });
  const hasManyStyles = originalHasManyStyles as unknown as CampaignCardStyles;
  const { component: Component, ...propsWithoutComponent } = campaignInfo;
  const { resources, id, joined } = campaignInfo;
  const autoJoin = useAutoJoin(campaignInfo.type);
  const { isFinished } = useCampaignStatus(id);

  const { isCollapsed, hasMany, backgroundImageUrl, arrow, handleArrowClick } =
    useCampaignCardState(resources, campaignInfo.type);

  // FLIP scaleY animation only for carousel cards (hasMany).
  // Single cards use CSS height transition — the arrow is a direct child
  // and scaleY counter-scaling causes it to detach during animation.
  const cardRef = useCollapseAnimation(isCollapsed, { disabled: !hasMany });

  // Single source of card visibility: observe the always-rendered card root.
  // Watching the root (not the bottom-info, which is hidden when collapsed / in
  // the ingame compact view) makes visibility correct in every context — lobby,
  // carousel, single view AND ingame — so sub-fetches start when the card is on
  // screen and stop when it's scrolled away or the view is exited.
  const setCampaignInfoVisible = useCampaignsStore((s) => s.setCampaignInfoVisible);
  const clearCampaignInfoVisible = useCampaignsStore((s) => s.clearCampaignInfoVisible);
  useEffect(() => {
    const el = cardRef.current;
    if (!el || !id) return;

    const observer = new IntersectionObserver(([entry]) => {
      setCampaignInfoVisible(id, entry.isIntersecting);
    }, { threshold: 0.1 });
    observer.observe(el);

    return () => {
      observer.disconnect();
      clearCampaignInfoVisible(id);
    };
  }, [id]);


  const showJoinButton = !autoJoin && !isCollapsed && (!isFinished || !!joined);
  const containerClasses = `${campaignCardStyles.campaignCard} ${isCollapsed ? campaignCardStyles.collapsed : ''} ${hasMany ? hasManyStyles.campaignCard : ''}`;

  const normalizedAssets = useMemo(
    () => normalizeCampaignAssets(resources, campaignInfo.type || PRIZE_POP_CAMPAIGN_TYPE),
    [resources, campaignInfo.type]
  );

    if (!Component) {
    return null;
  }

  return (
    <div
      ref={cardRef}
      className={containerClasses}
      style={!hasMany ? { '--campaign-card-gradient-color': normalizedAssets.colors.fill } as React.CSSProperties : undefined}
      onClick={onClick}
      {...attributes}
    >
      {!hasMany && <div className={campaignCardStyles.campaignCardShadow} {...attributes}></div>}
      <CampaignCardBackground
        backgroundImageUrl={backgroundImageUrl}
        hasMany={hasMany}
        isCollapsed={isCollapsed}
        campaignCardStyles={campaignCardStyles}
        hasManyStyles={hasManyStyles}
      />

      <CampaignCardButton
        campaignId={id}
        showButton={showJoinButton}
        campaignCardStyles={campaignCardStyles}
      />

      <Component {...propsWithoutComponent} />

      <CampaignCardControls
        hasMany={hasMany}
        arrow={arrow}
        isCollapsed={isCollapsed}
        onArrowClick={handleArrowClick}
        campaignCardStyles={campaignCardStyles}
      />
    </div>
  );
};

export default CampaignCard;
